import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_FULL, LOGO_ROUND } from "@/lib/constants";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { tx, getLang, UI_LANGS } from "@/i18n";
export default function Login() {
  const { login, setAnonLang } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(tx("Welkom terug!"));
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="/brand/costakey-zomer.webp?v=3" alt="Costa Key villa aan de Costa Blanca" className="absolute inset-0 h-full w-full object-cover object-center blur-[2px] scale-105" data-testid="login-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/85 via-secondary/20 to-transparent" />
        <div className="absolute bottom-10 left-10 text-white max-w-sm">
          <img src={LOGO_ROUND} alt="Costa Key" className="h-32 w-32 rounded-full shadow-2xl shadow-black/30 mb-5" />
          <h2 className="font-display font-bold text-3xl">{tx("Costa Key Portaal")}</h2>
          <p className="mt-2 text-white/90">{tx("Uw ogen, oren en handen aan de Costa Blanca Noord.")}</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center">
              <img src={LOGO_FULL} alt="Costa Key" className="h-14 w-auto object-contain" />
            </Link>
            <div className="flex gap-1" data-testid="login-language-picker">
              {UI_LANGS.map((l) => (
                <button key={l.value} type="button" onClick={() => setAnonLang(l.value)} title={l.label}
                  className={`h-7 px-2 rounded-md text-xs font-semibold uppercase transition-colors ${getLang() === l.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
                  data-testid={`login-lang-${l.value}`}>{l.value}</button>
              ))}
            </div>
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Inloggen")}</h1>
          <p className="text-muted-foreground mt-1 mb-6">{tx("Log in op uw portaal.")}</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">{tx("E-mailadres")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">{tx("Wachtwoord")}</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="login-password-input" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 bg-primary hover:bg-primary/90" data-testid="login-submit-btn">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Inloggen")}
          </Button>

          <p className="text-sm text-center mt-4">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-primary" data-testid="forgot-password-link">{tx("Wachtwoord vergeten?")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
